import { TextField, Typography } from "@mui/material";
import { useId } from "react";
import { Control, Controller } from "react-hook-form";
import {
  CreateProjectRequest,
  ManageProjectRequest,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useTranslator } from "../i18n/translator.ts";
import { describedBy } from "./describedBy.ts";
import { EmployeesMultiSelectionFormField } from "./EmployeesMultiSelectionFormField.tsx";
import { InfoToolTip } from "./InfoTooltip.tsx";
import { ProjectPermissionGroupsFormField } from "./ProjectPermissionGroupsFormField.tsx";

interface ProjectFormFieldsProps {
  control: Control<Omit<CreateProjectRequest | ManageProjectRequest, "id">>;
}

export const ProjectFormFields = ({ control }: ProjectFormFieldsProps) => {
  const translator = useTranslator();
  const displayNameHelpId = useId();
  const infoBoxHelpId = useId();
  const shepherdsHelpId = useId();
  const membersHelpId = useId();
  const signersHelpId = useId();

  return (
    <>
      <Controller
        name="displayName"
        control={control}
        rules={{ required: translator("displayNameRequired") }}
        render={({ field, fieldState }) => (
          <TextField
            label={
              <span>
                {translator("displayName")}
                <InfoToolTip
                  helpertext={translator("displayNameHelperText")}
                  ariaDescribedById={displayNameHelpId}
                ></InfoToolTip>
              </span>
            }
            {...field}
            variant="outlined"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                "aria-describedby": describedBy(
                  displayNameHelpId,
                  !!fieldState.error?.message && `${displayNameHelpId}-error`,
                ),
              },
              formHelperText: { id: `${displayNameHelpId}-error` },
            }}
          />
        )}
      />

      <Controller
        name="infoBox"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label={
              <span>
                {translator("infoBox")}
                <InfoToolTip
                  helpertext={translator("infoBoxHelperText")}
                  ariaDescribedById={infoBoxHelpId}
                ></InfoToolTip>
              </span>
            }
            {...field}
            variant="outlined"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                "aria-describedby": describedBy(
                  infoBoxHelpId,
                  !!fieldState.error?.message && `${infoBoxHelpId}-error`,
                ),
              },
              formHelperText: { id: `${infoBoxHelpId}-error` },
            }}
          />
        )}
      />

      <Typography variant="h6">
        <span>
          {translator("projectShepherds")}
          <InfoToolTip
            helpertext={translator("projectShepherdsHelperText")}
            ariaDescribedById={shepherdsHelpId}
          ></InfoToolTip>
        </span>
      </Typography>

      <ProjectPermissionGroupsFormField
        control={control}
        name="projectShepherds.group"
        ariaDescribedBy={shepherdsHelpId}
        labelText={translator("projectShepherdGroups")}
      />

      <EmployeesMultiSelectionFormField
        control={control}
        name="projectShepherds.individual"
        ariaDescribedBy={shepherdsHelpId}
        label={translator("projectShepherdIndividuals")}
      />

      <Typography variant="h6">
        <span>
          {translator("projectMembers")}
          <InfoToolTip
            helpertext={translator("projectMembersHelperText")}
            ariaDescribedById={membersHelpId}
          ></InfoToolTip>
        </span>
      </Typography>

      <ProjectPermissionGroupsFormField
        control={control}
        name="projectMembers.group"
        ariaDescribedBy={membersHelpId}
        labelText={translator("projectMemberGroups")}
      />

      <EmployeesMultiSelectionFormField
        control={control}
        name="projectMembers.individual"
        ariaDescribedBy={membersHelpId}
        label={translator("projectMemberIndividuals")}
      />

      <Typography variant="h6">
        <span>
          {translator("projectSigners")}
          <InfoToolTip
            helpertext={translator("projectSignersHelperText")}
            ariaDescribedById={signersHelpId}
          ></InfoToolTip>
        </span>
      </Typography>

      <ProjectPermissionGroupsFormField
        control={control}
        name="projectSigners.group"
        ariaDescribedBy={signersHelpId}
        labelText={translator("projectSignerGroups")}
      />

      <EmployeesMultiSelectionFormField
        control={control}
        name="projectSigners.individual"
        ariaDescribedBy={signersHelpId}
        label={translator("projectSignerIndividuals")}
      />
    </>
  );
};
